/**
 * Accountability Page
 *
 * Manager view showing per-employee task performance metrics.
 * Date range picker with summary cards and expandable employee rows.
 */

import { useState, useMemo } from 'react';
import {
  Card,
  Table,
  DatePicker,
  Space,
  Statistic,
  Tag,
  Typography,
  Progress,
  Row,
  Col,
  Drawer,
  List,
} from 'antd';
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  BarChartOutlined,
} from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import dayjs, { type Dayjs } from 'dayjs';
import { accountabilityApi, type UserStats, type UserTask } from '@api/accountability';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

export default function AccountabilityPage() {
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>([
    dayjs().startOf('month'),
    dayjs(),
  ]);
  const [selectedUser, setSelectedUser] = useState<{ id: string; name: string } | null>(null);

  const startDate = dateRange[0].format('YYYY-MM-DD');
  const endDate = dateRange[1].format('YYYY-MM-DD');

  const { data, isLoading } = useQuery({
    queryKey: ['accountability', startDate, endDate],
    queryFn: () => accountabilityApi.getStats(startDate, endDate),
  });

  const { data: userTasks, isLoading: tasksLoading } = useQuery({
    queryKey: ['accountability-tasks', selectedUser?.id, startDate, endDate],
    queryFn: () => accountabilityApi.getUserTasks(selectedUser!.id, startDate, endDate),
    enabled: !!selectedUser,
  });

  const summary = data?.summary;
  const completionRate = summary && summary.total_tasks > 0
    ? Math.round((summary.total_completed / summary.total_tasks) * 100)
    : 0;
  const onTimeRate = summary && summary.total_completed > 0
    ? Math.round((summary.total_on_time / summary.total_completed) * 100)
    : 0;

  const columns = useMemo(() => [
    {
      title: 'Employee',
      key: 'name',
      render: (_: unknown, record: UserStats) => (
        <a onClick={() => setSelectedUser({ id: record.assignee_id, name: record.user_name || record.user_email || 'Unknown' })}>
          {record.user_name || record.user_email || 'Unassigned'}
        </a>
      ),
    },
    {
      title: 'Total',
      dataIndex: 'total',
      sorter: (a: UserStats, b: UserStats) => a.total - b.total,
    },
    {
      title: 'Completed',
      dataIndex: 'completed',
      render: (val: number, record: UserStats) => {
        const pct = record.total > 0 ? Math.round((val / record.total) * 100) : 0;
        return (
          <Space>
            <span>{val}</span>
            <Progress percent={pct} size="small" style={{ width: 60 }} showInfo={false} />
          </Space>
        );
      },
    },
    {
      title: 'On Time',
      key: 'on_time',
      render: (_: unknown, record: UserStats) => {
        const onTime = record.completed - record.completed_late;
        return <Tag color="green">{onTime}</Tag>;
      },
    },
    {
      title: 'Late',
      dataIndex: 'completed_late',
      render: (val: number) => val > 0 ? <Tag color="orange">{val}</Tag> : <span>0</span>,
    },
    {
      title: 'Overdue',
      dataIndex: 'overdue',
      render: (val: number) => val > 0 ? <Tag color="red">{val}</Tag> : <span>0</span>,
    },
    {
      title: 'In Progress',
      dataIndex: 'in_progress',
      render: (val: number) => val > 0 ? <Tag color="blue">{val}</Tag> : <span>0</span>,
    },
    {
      title: 'Pending',
      dataIndex: 'pending',
      render: (val: number) => val > 0 ? <Tag>{val}</Tag> : <span>0</span>,
    },
  ], []);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <Title level={4} style={{ margin: 0 }}>
          <BarChartOutlined style={{ marginRight: 8 }} />
          Accountability
        </Title>
        <RangePicker
          value={dateRange}
          onChange={(dates) => {
            if (dates && dates[0] && dates[1]) {
              setDateRange([dates[0], dates[1]]);
            }
          }}
          allowClear={false}
        />
      </div>

      {/* Summary Cards */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic
              title="Total Tasks"
              value={summary?.total_tasks || 0}
              prefix={<BarChartOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic
              title="Completion Rate"
              value={completionRate}
              suffix="%"
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: completionRate >= 80 ? 'var(--status-success)' : completionRate >= 50 ? 'var(--status-warning)' : 'var(--status-error)' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic
              title="On-Time Rate"
              value={onTimeRate}
              suffix="%"
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: onTimeRate >= 80 ? 'var(--status-success)' : onTimeRate >= 50 ? 'var(--status-warning)' : 'var(--status-error)' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic
              title="Overdue"
              value={summary?.total_overdue || 0}
              prefix={<ExclamationCircleOutlined />}
              valueStyle={{ color: (summary?.total_overdue || 0) > 0 ? 'var(--status-error)' : undefined }}
            />
          </Card>
        </Col>
      </Row>

      {/* Per-Employee Table */}
      <Card>
        <Table
          columns={columns}
          dataSource={data?.items || []}
          rowKey="assignee_id"
          loading={isLoading}
          pagination={false}
          size="middle"
        />
      </Card>

      {/* User Task Detail Drawer */}
      <Drawer
        title={`Tasks: ${selectedUser?.name || ''}`}
        open={!!selectedUser}
        onClose={() => setSelectedUser(null)}
        width={500}
      >
        <List
          loading={tasksLoading}
          dataSource={userTasks?.items || []}
          renderItem={(task: UserTask) => (
            <List.Item>
              <List.Item.Meta
                title={
                  <Space>
                    <Text>{task.title}</Text>
                    {task.state === 'completed' && !task.is_late && (
                      <Tag color="green">On Time</Tag>
                    )}
                    {task.is_late && <Tag color="orange">Late</Tag>}
                    {task.is_overdue && <Tag color="red">Overdue</Tag>}
                    {task.state === 'in_progress' && <Tag color="blue">In Progress</Tag>}
                    {task.state === 'pending' && <Tag>Pending</Tag>}
                  </Space>
                }
                description={
                  <Space direction="vertical" size={0}>
                    <Text type="secondary">Due: {dayjs(task.due_date).format('MMM D, YYYY')}</Text>
                    {task.completed_at && (
                      <Text type="secondary">Completed: {dayjs(task.completed_at).format('MMM D, YYYY')}</Text>
                    )}
                    {task.template_name && (
                      <Text type="secondary" style={{ fontSize: 12 }}>{task.template_name}</Text>
                    )}
                  </Space>
                }
              />
            </List.Item>
          )}
        />
      </Drawer>
    </div>
  );
}
